<template>
  <div
    v-tooltip="disabled ? disabledReason : ''"
    class="mb-4 overflow-hidden bg-white sm:rounded-lg sm:shadow relative"
    :class="{ 'opacity-50 cursor-not-allowed': disabled }"
  >
    <div
      class="flex justify-between py-5 bg-white border-b border-gray-200 sm:px-6"
      :class="{ 'bg-orange-200': form.item_booked }"
    >
      <h3 class="flex justify-between flex-1 py-0 my-0 text-lg font-medium leading-6 leading-10 text-gray-900">
        <label
          class="flex items-center flex-grow block h-full mb-0"
          :class="{
            'cursor-not-allowed': disabled,
            'cursor-pointer': !disabled
          }"
        >
          <span
            class="relative block w-6 h-6 transition-colors duration-100 ease-out rounded-full shadow-inner"
            :class="{
              'bg-orange-100': !form.item_booked,
              'bg-green-400': form.item_booked,
            }"
          >
            <input
              v-model="form.item_booked"
              type="checkbox"
              class="absolute inset-0 invisible"
              style="margin: 0;"
              :disabled="disabled || isExpanded"
              @change="onBookedChange"
            >
          </span>
          <div class="flex-1 flex items-center gap-2">
            <span class=" block ml-3">{{ option.item_name }}</span>
          </div>
        </label>

        <div class="mr-3">
          <small class="text-sm text-gray-800">ergibt</small> {{ discount | price }}
        </div>
      </h3>

      <div class="absolute top-0 right-0 pr-1 pt-1 flex space-x-2 items-center">
        <template v-if="option.item_conditions && option.item_conditions.length > 0">
          <a
            v-for="condition in option.item_conditions"
            :key="condition.id"
            v-tooltip="`${condition.item_filename} [${formatFilesize(condition.item_filesize)}]`"
            :href="condition.item_file_url"
            target="_blank"
          >
            <mia-streamline-icon
              class="w-4 h-4 text-green-800 fill-current"
              src="office-file-pdf"
            />
          </a>
        </template>
        <div
          v-if="option.item_provision_comment"
          v-tooltip="option.item_provision_comment"
          class="text-blue-600 cursor-help"
        >
          <mia-streamline-icon
            class="w-4 h-4 fill-current"
            src="information-circle"
          />
        </div>
      </div>

      <div v-if="form.item_booked && option.item_editable">
        <span
          v-if="!isExpanded"
          class="inline-flex rounded-md shadow-sm"
        >
          <button
            type="button"
            class="relative inline-flex items-center px-4 py-2 text-sm font-medium leading-5 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:shadow-outline focus:border-blue-300 active:bg-gray-100 active:text-gray-800"
            @click="isExpanded = true"
          >
            Bearbeiten
          </button>
        </span>

        <template v-if="isExpanded">
          <span class="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              class="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium leading-5 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-blue-200 hover:border-blue-200 hover:text-gray-600 focus:outline-none focus:shadow-outline focus:border-blue-300 active:bg-gray-100 active:text-gray-800"
              @click="onCancel"
            >
              Einklappen
            </button>
          </span>
        </template>
      </div>
    </div>
    <mia-collapsible-content
      :show="isExpanded"
      @expanded="onExpanded"
    >
      <div
        v-if="option.item_origin > 0"
        class="p-4 mb-4"
      >
        <mia-notice permanent>
          Dies ist eine Korrekturbuchung
        </mia-notice>
      </div>

      <div
        v-if="option.item_deprecated !== '0000-00-00 00:00:00' && option.item_deprecated !== ''"
        class="p-4 mb-4"
      >
        <mia-notice
          permanent
          type="warning"
        >
          Diese Buchung ist nicht mehr aktuell und wurde durch eine Korrekturbuchung ersetzt
        </mia-notice>
      </div>

      <div
        v-if="usages.length > 0"
        class="px-4 mb-4"
      >
        <mia-notice
          permanent
          type="info"
        >
          <p
            v-for="usedProvision in usages"
            :key="usedProvision.id"
          >
            Bereits verbucht am {{ usedProvision.item_created }} mit {{ usedProvision.item_provision | price('EUR') }}
          </p>
        </mia-notice>
      </div>

      <div class="flex">
        <div class="flex-1 p-4">
          <mia-form-section-grid>
            <div>
              <mia-feature-input
                v-slot:default="{ id, hasErrors }"
                label="Fixbetrag netto"
              >
                <mia-money-input
                  :id="id"
                  ref="fixInput"
                  v-model="form.item_fix"
                  :has-errors="hasErrors"
                  style="height: 39px;"
                  @input="emitUpdate"
                />
              </mia-feature-input>
            </div>

            <div>
              <mia-feature-input
                v-model="form.item_percent"
                label="% der UPE"
                type="number"
                step="0.01"
                min="0"
                @input="emitUpdate"
              />
            </div>

            <div class="col-span-2">
              <mia-feature-textarea
                v-model="form.item_comment"
                label="Kommentar (optional)"
                rows="2"
                @input="emitUpdate"
              />
            </div>

            <div>
              <mia-feature-toggle
                v-model="isForDealer"
                label="An Händler auszahlen"
                label-position="top"
                @input="onForDealerInput"
              />
            </div>

            <div v-if="isForDealer">
              <mia-feature-select
                v-model="form.item_fordealer_paymode"
                label="Händlerauszahlung"
                :options="payoutOptions"
                track-by="id"
                name-by="label"
                @input="emitUpdate"
              />
            </div>
          </mia-form-section-grid>
        </div>

        <div class="p-4">
          <mia-feature-toggle
            v-model="isPoolTarget"
            label="Buchen auf"
            label-position="top"
            is-neutral
            :labels="{
              unchecked: 'Fahrzeug',
              checked: 'Gesamtpool',
            }"
            :width="130"
            @input="onTargetInput"
          />
        </div>
      </div>
    </mia-collapsible-content>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import FeatureTextarea from '@/components/cars/edit/FeatureTextarea.vue'
import FeatureToggle from '@/components/cars/edit/FeatureToggle.vue'
import FormSectionGrid from '@/components/ui/widgets/FormSectionGrid.vue'
import MoneyInput from '@/components/ui/inputs/MoneyInput.vue'
import Notice from '@/components/cars/edit/Notice.vue'
import { calculateProvisionValue, cashSave } from '@/helpers/utils'
import * as filesize from 'filesize'

export default {
  name: 'MiaProvisionOptionCard',

  components: {
    'mia-feature-input': FeatureInput,
    'mia-form-section-grid': FormSectionGrid,
    'mia-money-input': MoneyInput,
    'mia-feature-textarea': FeatureTextarea,
    'mia-feature-toggle': FeatureToggle,
    'mia-feature-select': FeatureSelect,
    'mia-notice': Notice
  },

  props: {
    option: {
      type: Object,
      required: true
    },

    price: {
      type: [String, Number],
      default: 0
    },

    netListPrice: {
      type: [String, Number],
      default: 0
    },

    disabled: {
      type: Boolean,
      default: false
    },

    disabledReason: {
      type: String,
      default: 'Diese Prämie kann nicht in Kombination mit einer anderen Prämie ausgewählt werden.'
    }
  },

  data() {
    return {
      isExpanded: false,
      fileEndpoint: `${process.env.VUE_APP_PROVIDER}/uploads/load/`,
      form: {
        item_booked: false,
        item_fix: '',
        item_percent: '',
        item_comment: '',
        item_target: 'car',
        item_fordealer: 'N',
        item_fordealer_paymode: 'direct'
      },
      isPoolTarget: false,
      isForDealer: false,
      payoutOptions: [
        {
          id: 'direct',
          label: 'per Direktvergütung'
        },
        {
          id: 'monthly',
          label: 'per Monatsabrechnung'
        }
      ],
      originalOption: cloneDeep(this.option)
    }
  },

  computed: {
    discount() {
      let fix = this.originalOption.item_fix

      // Fix the fix
      if (typeof fix === 'string' && fix.includes('.') && !fix.includes(',') && /[.]\d{1,}$/.test(fix)) {
        fix = fix.replace('.', ',')
      }

      let percent = this.originalOption.item_percent
      let customFix = this.form.item_fix

      // Fix the fix
      if (typeof customFix === 'string' && customFix.includes('.') && !customFix.includes(',') && /[.]\d{1,}$/.test(customFix)) {
        customFix = customFix.replace('.', ',')
      }

      let customPercent = this.form.item_percent

      fix = (fix === null || fix === '' || fix === '0') ? '' : cashSave(fix)
      percent = (percent === null || percent === '' || percent === '0') ? '' : parseFloat(percent)
      customFix = (customFix === null || customFix === '' || customFix === '0') ? '' : cashSave(customFix)
      customPercent = (customPercent === null || customPercent === '' || customPercent === '0') ? '' : parseFloat(customPercent)

      if (customFix === fix) {
        customFix = ''
      }

      if (customPercent === percent) {
        customPercent = ''
      }

      return calculateProvisionValue({
        fix,
        percent,
        customProvisionFix: customFix,
        customProvisionPercent: customPercent,
        netListPrice: this.netListPrice
      })
    },

    usages() {
      return this.$store.state.provisions.usedProvisions.filter(usedProvision => parseInt(usedProvision.item_provision_id, 10) === parseInt(this.option.id, 10))
    }
  },

  watch: {
    option: {
      immediate: true,
      deep: true,
      handler(option) {
        this.initForm(option)

        if (this.isExpanded && !option.item_booked) {
          this.isExpanded = false
        }
      }
    }
  },

  methods: {
    initForm(option = this.option) {
      this.form.item_booked = option.item_booked
      this.form.item_fix = option.item_fix
      this.form.item_percent = option.item_percent
      this.form.item_comment = option.item_comment
      this.form.item_target = option.item_target
      this.form.item_fordealer = option.item_fordealer
      this.form.item_fordealer_paymode = option.item_fordealer_paymode

      this.isPoolTarget = this.form.item_target === 'pool'
      this.isForDealer = this.form.item_fordealer === 'Y'
    },

    collapse() {
      this.isExpanded = false
    },

    emitUpdate() {
      this.$emit('update', {
        ...this.option,
        ...this.form
      })
    },

    onExpanded() {
      this.$nextTick(() => {
        this.$refs.fixInput.$el.querySelector('input').focus()
      })
    },

    onTargetInput(isPoolTarget) {
      this.form.item_target = isPoolTarget ? 'pool' : 'car'
      this.emitUpdate()
    },

    onForDealerInput(isForDealer) {
      this.form.item_fordealer = isForDealer ? 'Y' : 'N'
      this.emitUpdate()
    },

    onCancel() {
      this.collapse()
    },

    onBookedChange() {
      this.emitUpdate()
      this.collapse()
    },

    formatFilesize(bytes) {
      return filesize(bytes)
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/ input[type="number"].form-input {
  @apply text-base;
  @apply leading-normal;

  height: 39px;
}
</style>
